import { create } from "zustand";
import type {
  TripTrackingEvent,
  TripStatusResponse,
} from "../../socket";
import { useActiveTripsStore } from "./activeTripsStore";

const UNKNOWN_TRIP_EVENT_MAX_AGE_MS = 5 * 60 * 1000;
const UNKNOWN_TRIP_FETCH_THROTTLE_MS = 30 * 1000;
let lastUnknownTripFetchAt = 0;

function syncActiveTripsFromEvent(event: TripTrackingEvent) {
  if (!event.trip_id) return;
  const activeTrips = useActiveTripsStore.getState();

  if (event.trip_status === "COMPLETED") {
    activeTrips.markTripCompleted(event.trip_id, event.device_id);
    activeTrips.fetchActiveTrips(true);
    return;
  }

  const isKnown = activeTrips.trips.some((t) => t.id === event.trip_id);
  const isRecent =
    Date.now() - event.timestamp < UNKNOWN_TRIP_EVENT_MAX_AGE_MS;
  const throttled =
    Date.now() - lastUnknownTripFetchAt < UNKNOWN_TRIP_FETCH_THROTTLE_MS;

  if (!isKnown && isRecent && !throttled) {
    lastUnknownTripFetchAt = Date.now();
    activeTrips.fetchActiveTrips(true);
  }
}

export interface DeviceTripState {
  device_id: string;
  trip_id: string | null;
  trip_title: string | null;
  route_id: string;
  route_name: string;
  company_id: string;
  trip_status: "NOT_STARTED" | "IN_PROGRESS" | "COMPLETED";
  last_event: TripTrackingEvent["event_type"];
  last_event_time: number;
  is_deviated: boolean;
  message: string;
  device_name: string | null;
  vehicle_name: string | null;
}

interface TripTrackingStore {
  byDeviceId: Record<string, DeviceTripState>;
  byTripId: Record<string, DeviceTripState>;
  eventsByDevice: Record<string, TripTrackingEvent[]>;
  maxEventsPerDevice: number;

  updateFromEvent: (event: TripTrackingEvent) => void;
  updateFromTripStatus: (response: TripStatusResponse) => void;
  getDeviceTripEvents: (deviceId: string) => TripTrackingEvent[];
  clear: () => void;
}

export const useTripTrackingStore = create<TripTrackingStore>((set, get) => ({
  byDeviceId: {},
  byTripId: {},
  eventsByDevice: {},
  maxEventsPerDevice: 50,

  updateFromEvent: (event: TripTrackingEvent) => {
    const deviceState: DeviceTripState = {
      device_id: event.device_id,
      trip_id: event.trip_id,
      trip_title: event.trip_title,
      route_id: event.route_id,
      route_name: event.route_name,
      company_id: event.company_id,
      trip_status: event.trip_status,
      last_event: event.event_type,
      last_event_time: event.timestamp,
      is_deviated: event.is_deviated,
      message: event.message,
      device_name: event.device_name,
      vehicle_name: event.vehicle_name,
    };

    set((state) => {
      const currentEvents = state.eventsByDevice[event.device_id] || [];

      // Deduplicate: RTDB `onValue` refires with the full snapshot every time
      // a new event is pushed, so the subscription re-sends previously-seen
      // events. Without this guard, each snapshot refresh would prepend every
      // event again and the history would show duplicates (same timestamp +
      // event_type). We dedupe on (timestamp, event_type, trip_id) because
      // RTDB push-ids are not guaranteed to reach the client in order.
      const isDuplicate = currentEvents.some(
        (e) =>
          e.timestamp === event.timestamp &&
          e.event_type === event.event_type &&
          e.trip_id === event.trip_id,
      );
      const updatedEvents = isDuplicate
        ? currentEvents
        : [event, ...currentEvents].slice(0, state.maxEventsPerDevice);

      const newByDeviceId = { ...state.byDeviceId, [event.device_id]: deviceState };
      const newByTripId = event.trip_id
        ? { ...state.byTripId, [event.trip_id]: deviceState }
        : state.byTripId;

      return {
        byDeviceId: newByDeviceId,
        byTripId: newByTripId,
        eventsByDevice: {
          ...state.eventsByDevice,
          [event.device_id]: updatedEvents,
        },
      };
    });

    syncActiveTripsFromEvent(event);
  },

  updateFromTripStatus: (response: TripStatusResponse) => {
    if (!response.states || !response.deviceId) return;

    set((state) => {
      const newByDeviceId = { ...state.byDeviceId };
      const newByTripId = { ...state.byTripId };

      for (const s of response.states) {
        if (s.status === "NOT_STARTED" && !s.trip_id) continue;

        const deviceState: DeviceTripState = {
          device_id: response.deviceId,
          trip_id: s.trip_id,
          trip_title: s.trip_title ?? null,
          route_id: s.route_id,
          route_name: s.route_name ?? "",
          company_id: "",
          trip_status: s.status,
          last_event: s.is_deviated
            ? "DESVIO_DE_RUTA"
            : s.status === "COMPLETED"
              ? "VIAJE_COMPLETADO"
              : "VIAJE_INICIADO",
          last_event_time: s.completed_at || s.started_at || Date.now(),
          is_deviated: s.is_deviated ?? false,
          message: "",
          device_name: null,
          vehicle_name: null,
        };

        const existing = newByDeviceId[response.deviceId];
        if (existing && existing.last_event_time > deviceState.last_event_time) continue;

        newByDeviceId[response.deviceId] = deviceState;
        if (s.trip_id) newByTripId[s.trip_id] = deviceState;
      }

      return { byDeviceId: newByDeviceId, byTripId: newByTripId };
    });
  },

  getDeviceTripEvents: (deviceId: string) => {
    return get().eventsByDevice[deviceId] || [];
  },

  clear: () => {
    set({ byDeviceId: {}, byTripId: {}, eventsByDevice: {} });
  },
}));
